import { Component, effect, inject, input, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { ExpenseSettlement, ExpenseSettlementStatus } from '../../../models/expense-settlement/expense-settlement';
import { ExpenseSettlementProxyService } from '../../../proxies/expense-settlement.proxy.service';
import { Currency, currencySymbol } from '../../../utilities/currency.util';
import { cashDocumentStatusLabel, cashDocumentStatusSeverity } from '../cash-document-status.util';

/** The latest settlements shown on a fund's or trip's page. */
const OWNER_SETTLEMENTS_LIMIT = 10;

/** The expense settlements of one fund or trip (#5799), newest first, for its details page. */
@Component({
  selector: 'app-owner-settlements',
  templateUrl: './owner-settlements.component.html',
})
export class OwnerSettlementsComponent {
  private readonly proxy = inject(ExpenseSettlementProxyService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);

  /** The fund's or trip's id. */
  readonly ownerReference = input.required<string>();

  readonly isLoading = signal(false);
  readonly settlements = signal<ExpenseSettlement[]>([]);

  constructor() {
    effect(() => this.load(this.ownerReference()), { allowSignalWrites: true });
  }

  private load(ownerReference: string): void {
    this.isLoading.set(true);
    this.proxy
      .search({ ownerReference }, 1, OWNER_SETTLEMENTS_LIMIT, null, -1)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe((response) => this.settlements.set(response.data?.items ?? []));
  }

  open(settlement: ExpenseSettlement): void {
    this.router.navigate(['/expense-settlement-details'], { queryParams: { id: settlement.id } });
  }

  statusLabel(status: ExpenseSettlementStatus): string {
    return cashDocumentStatusLabel(status, (key, fallback) => {
      const value = this.translate.instant(key);
      return value === key ? fallback : value;
    });
  }

  statusSeverity(status: ExpenseSettlementStatus): 'success' | 'warn' | 'secondary' {
    return cashDocumentStatusSeverity(status);
  }

  symbolOf(currency: Currency): string {
    return currencySymbol(currency);
  }

  getRow(rowData: unknown): ExpenseSettlement {
    return rowData as ExpenseSettlement;
  }
}
